// webapp/src/Profile.js
import React, { useState, useEffect, useCallback } from "react"; // Added useCallback import
import { useNavigate } from "react-router-dom";
import { supabase } from "./supabase";
import { useAuth } from "./AuthContext";
import Modal from "./Modal";
import { 
  FiUser, 
  FiMail, 
  FiLock, 
  FiLogOut, 
  FiAlertCircle,
  FiRefreshCw,
  FiTrash2, 
  FiCheckCircle,
  FiCreditCard,
  FiUpload,
  FiInfo
} from "react-icons/fi";
import "./Profile.css";
const CONFIG = require("./config");

function Profile() {
  const [currentUser, setCurrentUser] = useState(null);
  const [quota, setQuota] = useState(null);
  const [isPremium, setIsPremium] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [responseMessage, setResponseMessage] = useState(null);
  // Add missing modal state variables
  const [modalTitle, setModalTitle] = useState('');
  const [modalContent, setModalContent] = useState('');
  const [showModal, setShowModal] = useState(false);
  
  const navigate = useNavigate();
  const { resetReauthenticationStatus, setNextPageAfterReauth, checkPremiumStatus } = useAuth();

  // Get user data
  useEffect(() => {
    async function fetchUserData() {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        // Determine if user signed up with email/password
        const isEmailSignIn = user.app_metadata?.provider === 'email' || false;
        
        setCurrentUser({
          email: user.email,
          emailVerified: user.email_confirmed_at !== null,
          isEmailSignIn,
        });
      } else {
        setCurrentUser(null);
      }
    }
    
    fetchUserData();
  }, []);

  // Check premium status
  useEffect(() => {
    async function checkUserPremium() {
      if (currentUser) {
        const isPremiumUser = await checkPremiumStatus();
        setIsPremium(isPremiumUser);
      } else {
        setIsPremium(false);
      }
    }
    
    checkUserPremium();
  }, [currentUser, checkPremiumStatus]);

  // Fetch quota if on the free plan - wrapped in useCallback to prevent infinite renders
  const fetchQuota = useCallback(async () => {
    if (!currentUser) return;
    
    try {
      const { data, error } = await supabase
        .from('user_quotas')
        .select('*')
        .single();
        
      if (error) throw error;
      
      setQuota(data);
    } catch (error) {
      console.error("Error fetching quota:", error);
    }
  }, [currentUser]); // Include currentUser as dependency

  useEffect(() => {
    if (currentUser && !isPremium) {
      fetchQuota();
    }
  }, [currentUser, isPremium, fetchQuota]);  

  // Handlers
  const handleSignOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error("Sign out error:", error);
    } else {
      resetReauthenticationStatus();
      navigate("/login");
    }
  };

  const requestEmailVerification = async () => {
    if (currentUser && !currentUser.emailVerified) {
      try {
        const { error } = await supabase.auth.resend({
          type: 'signup',
          email: currentUser.email
        });
        
        if (error) throw error;
        alert("Verification email sent.");
      } catch (error) {
        console.error("Error sending verification email:", error);
      }
    }
  };

  const handleChangePasswordClick = () => {
    if (currentUser.isEmailSignIn) {
      setNextPageAfterReauth("/dashboard/new-password");
      navigate("/dashboard/reauthenticate");
    }
  };

  const handleChangeEmailClick = () => {
    if (currentUser.isEmailSignIn) {
      setNextPageAfterReauth("/dashboard/change-email");
      navigate("/dashboard/reauthenticate");
    }
  };

  const handleResetAccountData = () => {
    // Open the confirmation modal
    setIsModalOpen(true);
    setResponseMessage(null);
  };

  const handleResetConfirm = async () => {
    setIsProcessing(true);
    
    try {
      // Call the backend API to reset the account data
      const { data, error } = await supabase.functions.invoke('reset-account-data');
      
      if (error) throw error;
      
      setResponseMessage({
        type: "success",
        text: data.message || "Your account data has been reset successfully."
      });
    } catch (error) {
      console.error("Error resetting account data:", error);
      setResponseMessage({
        type: "error",
        text: error.message || "Failed to reset account data. Please try again."
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setResponseMessage(null);
  };

  const handleDeleteAccount = () => {
    setNextPageAfterReauth("/dashboard/delete-account-confirm");
    navigate("/dashboard/reauthenticate");
  };

  const handlePremiumSubscription = async () => {
    try {
      // Get the session token
      const { data } = await supabase.auth.getSession();
      
      if (!data.session) {
        // Try to refresh session if not available
        const { data: refreshData } = await supabase.auth.refreshSession();
        if (!refreshData.session) {
          throw new Error("No active session");
        }
      }
      
      const token = data.session.access_token;
      
      console.log("Starting premium subscription process...");
      
      const response = await fetch(
        CONFIG.CREATE_CHECKOUT_SESSION,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          // No need to send user ID - it will be extracted from the token
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Subscription error");
      }
      
      const checkoutData = await response.json();
      window.location.href = checkoutData.url;
    } catch (error) {
      console.error("Premium subscription error:", error);
      setModalTitle("Premium Subscription Error");
      setModalContent("Subscription failed: " + error.message);
      setShowModal(true);
    }
  };

const handleManageSubscription = async () => {
  try {
    // Get the session token
    const { data } = await supabase.auth.getSession();
    
    if (!data.session) {
      throw new Error("No active session");
    }
    
    const token = data.session.access_token;
    
    const response = await fetch(
      CONFIG.CREATE_BILLINGPORTAL_SESSION,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        }
      }
    );
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Billing portal error");
    }
    
    const billingData = await response.json();
    window.location.href = billingData.url;
  } catch (error) {
    console.error("Error managing subscription:", error);
    // Show error message to user
    setModalTitle("Subscription Management Error");
    setModalContent("Failed to access billing portal: " + error.message);
    setShowModal(true);
  }
};

  // Progress bar for daily quota (if free)
  let progressPercent = 0;
  if (quota && quota.limit !== "unlimited") {
    progressPercent = (quota.used / quota.limit) * 100;
    if (progressPercent > 100) progressPercent = 100; // cap at 100
  }

  return (
    <div className="profile-container">
      <div className="profile-header">
        <h1>User Profile</h1>
        <p>Manage your account settings and subscription</p>
      </div>

      {currentUser ? (
        <div className="profile-cards-wrapper">
          {/* -- Plan / User Info Card -- */}
          <div className="profile-card">
            <div className="section-title">User Information</div>
            
            <div className="user-info-item">
              <FiUser />
              <strong>Email:</strong> {currentUser.email}
            </div>

            {/* Plan Info */}
            {isPremium ? (
              <>
                <div className="premium-text">
                  <FiCheckCircle />
                  <span>Premium Subscription (Unlimited Transcriptions)</span>
                </div>
                
                <div className="info-text">
                  <FiInfo />
                  <span>You have access to all premium features including longer transcriptions and priority support.</span>
                </div>
                
                <div className="form-group">
                  <button className="btn primary-btn" onClick={handleManageSubscription}>
                    <FiCreditCard /> Manage Subscription
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="freeplan-text">
                  <strong>Free Plan</strong>
                  {quota && (
                    <div>  
                      Daily usage: {quota.used} of {quota.limit} transcriptions
                      ({quota.remaining} remaining today)
                    </div>
                  )}
                </div>

                {/* Progress bar with color coding based on usage */}
                {quota && quota.limit !== "unlimited" && (
                  <div className="quota-bar-container">
                    <div
                      className={`quota-bar-fill ${
                        progressPercent > 80 ? 'quota-high' : 
                        progressPercent > 50 ? 'quota-medium' : 'quota-low'
                      }`}
                      style={{ width: `${progressPercent}%` }}
                    ></div>
                  </div>
                )}

                <div className="form-group">
                  <button className="btn upgrade-btn" onClick={handlePremiumSubscription}>
                    <FiUpload /> Upgrade to Premium
                  </button>
                </div>
              </>
            )}

            <div className="form-group">
              <button className="btn secondary-btn" onClick={handleSignOut}>
                <FiLogOut /> Sign Out
              </button>
            </div>
          </div>

          {/* -- Email & Password Settings (if email sign-in) -- */}
          {currentUser && currentUser.isEmailSignIn && (
            <div className="profile-card">
              <div className="section-title">Security & Email</div>

              {/* Email verified status + resend */}
              {!currentUser.emailVerified && (
                <div className="warning-text">
                  <FiAlertCircle />
                  <span>Your email is not verified. Please verify your email for account security.</span>
                </div>
              )}
              
              {!currentUser.emailVerified && (
                <div className="form-group">
                  <button className="btn secondary-btn" onClick={requestEmailVerification}>
                    <FiMail /> Resend Verification Email
                  </button>
                </div>
              )}
              
              <div className="form-group">
                <button className="btn secondary-btn" onClick={handleChangeEmailClick}>
                  <FiMail /> Change Email
                </button>
              </div>

              {/* Password */}
              <div className="form-group">
                <button className="btn secondary-btn" onClick={handleChangePasswordClick}>
                  <FiLock /> Change Password
                </button>
              </div>
            </div>
          )}

          {/* -- Account Data Card (Reset/Delete) -- */}
          <div className="profile-card">
            <div className="section-title">Account Data</div>
            
            <div className="warning-text">
              <FiAlertCircle />
              <span>The following actions will affect your stored data and cannot be undone.</span>
            </div>
            
            <div className="form-group">
              <button className="btn danger-btn" onClick={handleResetAccountData}>
                <FiRefreshCw /> Reset Account Data
              </button>
            </div>
            <div className="form-group">
              <button className="btn danger-btn" onClick={handleDeleteAccount}>
                <FiTrash2 /> Delete Account
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="profile-card">
          <div className="warning-text">
            <FiAlertCircle />
            <span>No user is logged in. Please log in to view your profile.</span>
          </div>
        </div>
      )}

      {/* Reset Account Confirmation Modal */}
      <Modal
        isOpen={isModalOpen}
        title="Reset Account Data"
        message="Are you sure you want to reset your account data? All your transcription history will be removed. This action cannot be undone."
        onClose={handleCloseModal}
        onConfirm={handleResetConfirm}
        isProcessing={isProcessing}
        responseMessage={responseMessage}
      />
      
      {/* Additional modal for subscription errors */}
      {showModal && (
        <Modal
          isOpen={showModal}
          title={modalTitle}
          message={modalContent}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}

export default Profile;